// Portfolio ROI model — single source of truth shared by the Affordable Housing
// ROI calculator and the pipeline "Generate ROI Report" feature, so the numbers
// always agree. All figures are unit-driven estimates (not quotes).
import { doorFeeIncome, doorFeeRpu } from './doorFee';

// Per-project / per-unit constants — mirror src/main-component/AffordableHousingPage.
export const WIFI_VALUE = 5980;        // per project: ISP-provided WiFi hardware + yr-1 internet retail value
export const INFRA_SAVINGS_PU = 339;   // per unit: 242 fiber/cabling + 32 DAS/ERCCS + 65 surveillance design
export const CONSULT_FEE_PU = 310;     // per unit: BUILDLAB design & coordination
export const PROFIT_SHARE_100 = 8;     // % ongoing ISP profit share, 100+ unit tier
export const PROFIT_SHARE_BASE = 7;    // % ongoing ISP profit share, 40-99 tier

export function projectRoi(p) {
    const units = p.totalUnits || 0;
    const doorFee = doorFeeIncome(units);             // one-time ISP door fee
    const wifi = units > 0 ? WIFI_VALUE : 0;          // complimentary WiFi retail value
    const infra = Math.round(units * INFRA_SAVINGS_PU); // design/material savings
    const fee = Math.round(units * CONSULT_FEE_PU);   // BUILDLAB fee
    const totalValue = doorFee + wifi + infra;        // value generated
    const net = totalValue - fee;                     // net of BUILDLAB fee
    return {
        units,
        doorFee,
        wifi,
        infra,
        fee,
        totalValue,
        net,
        rpu: doorFeeRpu(units),
        profitPct: units >= 100 ? PROFIT_SHARE_100 : PROFIT_SHARE_BASE,
    };
}

export function portfolioRoi(projects) {
    const rows = projects.map((p) => ({ ...p, roi: projectRoi(p) }));
    const agg = rows.reduce(
        (a, r) => {
            a.units += r.roi.units;
            a.doorFee += r.roi.doorFee;
            a.wifi += r.roi.wifi;
            a.infra += r.roi.infra;
            a.fee += r.roi.fee;
            a.totalValue += r.roi.totalValue;
            a.net += r.roi.net;
            return a;
        },
        { units: 0, doorFee: 0, wifi: 0, infra: 0, fee: 0, totalValue: 0, net: 0 }
    );
    agg.count = rows.length;
    agg.counties = new Set(projects.map((p) => p.county).filter(Boolean)).size;
    agg.cities = new Set(projects.map((p) => p.city).filter(Boolean)).size;
    // Profit-share tier is per-property: 8% at the 100+ unit tier, 7% at 40-99.
    // A mixed portfolio earns both, so report the split rather than one blended %.
    agg.tier8 = rows.filter((r) => r.roi.units >= 100).length;   // 8% — 100+ units
    agg.tier7 = rows.length - agg.tier8;                          // 7% — under 100 units
    agg.profitRange = agg.tier8 && agg.tier7 ? '7–8%' : agg.tier8 ? '8%' : '7%';
    // ROI % = net value (value generated − fee) ÷ BUILDLAB fee.
    agg.roiPct = agg.fee > 0 ? Math.round((agg.net / agg.fee) * 100) : 0;
    return { rows, agg };
}

export const fmtUSD = (n) => '$' + Math.round(n || 0).toLocaleString();
export const fmtUSDshort = (n) => {
    const v = Math.round(n || 0);
    if (v >= 1e6) return '$' + (v / 1e6).toFixed(1) + 'M';
    if (v >= 1e3) return '$' + Math.round(v / 1e3) + 'K';
    return '$' + v;
};
